import type { TFunction } from "i18next";
import { currentLanguage, LOCALES } from "@/i18n";

export interface Plan {
  name: string;
  /** Display price, e.g. "€9.99" */
  price: string;
  /** Amount in cents — the single source of truth for the checkout total. */
  priceCents: number;
  period: string;
  tagline: string;
  features: string[];
  /** The perks in `features` that don't depend on volume — what a custom amount reaching this plan also gets. */
  benefits: string[];
  highlighted: boolean;
  cta: string;
  credits: number;
}

export const plans: Plan[] = [
  {
    name: "Starter",
    price: "€9.99",
    priceCents: 999,
    period: "one-time",
    tagline: "For creators getting started with AI visuals.",
    features: [
      "20 points — up to 20 images or 10 video clips",
      "Images up to 1024 × 1024 px",
      "Video clips up to Full HD (1080p)",
      "Commercial usage license",
      "Email support",
    ],
    benefits: ["Commercial usage license", "Email support"],
    highlighted: false,
    cta: "Start with Starter",
    credits: 20,
  },
  {
    name: "Pro",
    price: "€29.99",
    priceCents: 2999,
    period: "one-time",
    tagline: "For professionals who need speed and polish.",
    features: [
      "100 points — up to 100 images or 50 video clips",
      "Images up to 1024 × 1024 px",
      "Video clips up to Full HD (1080p)",
      "Commercial usage license",
      "Priority email support",
    ],
    benefits: ["Commercial usage license", "Priority email support"],
    highlighted: true,
    cta: "Go Pro",
    credits: 100,
  },
  {
    name: "Elite",
    price: "€79.99",
    priceCents: 7999,
    period: "one-time",
    tagline: "For studios and teams at full scale.",
    features: [
      "300 points — up to 300 images or 150 video clips",
      "Images up to 1024 × 1024 px",
      "Video clips up to Full HD (1080p)",
      "Commercial usage license",
      "Priority email and phone support",
    ],
    benefits: ["Commercial usage license", "Priority email and phone support"],
    highlighted: false,
    cta: "Scale with Elite",
    credits: 300,
  },
];

export const DEFAULT_PLAN = "Pro";

export function getPlan(name?: string | null): Plan {
  return (
    plans.find((p) => p.name.toLowerCase() === (name ?? "").toLowerCase()) ??
    plans.find((p) => p.name === DEFAULT_PLAN)!
  );
}

/** "€9.99" in English, "9,99 €" in Bulgarian — follows the active language. */
export function formatEur(cents: number): string {
  return new Intl.NumberFormat(LOCALES[currentLanguage()], {
    style: "currency",
    currency: "EUR",
  }).format(cents / 100);
}

/**
 * A plan's customer-facing text in the active language. `name` stays as is:
 * it is the plan's id on the server and a brand name in both languages.
 */
export function planText(t: TFunction, plan: Plan) {
  const key = `plans.${plan.name.toLowerCase()}`;
  return {
    price: formatEur(plan.priceCents),
    period: t("plans.period"),
    tagline: t(`${key}.tagline`),
    cta: t(`${key}.cta`),
    features: t(`${key}.features`, { returnObjects: true }) as string[],
    credits: t("plans.creditsIncluded", { count: plan.credits }),
  };
}

// ── Custom plan ────────────────────────────────────────────────────────────
// The customer picks the price. The server (server/src/lib/plans.ts,
// customPlan) decides what it buys; this mirror only previews it, so keep the
// two in step.

export const CUSTOM_PLAN = "Custom";
export const CUSTOM_MIN_CENTS = 1000;
export const CUSTOM_MAX_CENTS = 200000;

export interface BenefitsLevel {
  name: string;
  benefits: string[];
}

/**
 * Reached only by a custom amount — there is no Studio plan to buy. It earns
 * credits at Elite's rate and adds perks on top of Elite's.
 */
export const STUDIO_MIN_CENTS = 25000;
export const STUDIO_LEVEL: BenefitsLevel = {
  name: "Studio",
  benefits: ["Everything in Elite", "Dedicated account manager", "Custom invoicing"],
};

export interface CustomPlanPreview {
  amountCents: number;
  credits: number;
  /** The benefits this amount unlocks. */
  level: BenefitsLevel;
}

/**
 * Benefits of the highest level the amount reaches, and credits at the rate of
 * the highest plan it reaches, rounded down — exactly a plan's price buys
 * exactly that plan, and every euro above Elite keeps earning at Elite's rate.
 */
export function customPlan(amountCents: number): CustomPlanPreview {
  const byPrice = [...plans].sort((a, b) => a.priceCents - b.priceCents);
  const rate = [...byPrice].reverse().find((p) => amountCents >= p.priceCents) ?? byPrice[0];
  const credits = Math.floor((amountCents * rate.credits) / rate.priceCents);
  const level = amountCents >= STUDIO_MIN_CENTS ? STUDIO_LEVEL : rate;
  return { amountCents, credits, level };
}

/** "25", "25.5" or "25,50" → 2550 cents; null for anything else. */
export function parseEurToCents(input: string): number | null {
  const value = input.trim().replace(",", ".");
  if (!/^\d{1,6}(\.\d{1,2})?$/.test(value)) return null;
  return Math.round(Number(value) * 100);
}

/** Why an amount can't be used, in the active language, or null when it can. */
export function customAmountError(t: TFunction, cents: number | null): string | null {
  if (cents === null) return t("pricing.custom.errors.invalid");
  if (cents < CUSTOM_MIN_CENTS || cents > CUSTOM_MAX_CENTS) {
    return t("pricing.custom.errors.range", {
      min: formatEur(CUSTOM_MIN_CENTS),
      max: formatEur(CUSTOM_MAX_CENTS),
    });
  }
  return null;
}

/** A benefits level's perks in the active language (level names are brand names). */
export function benefitsText(t: TFunction, level: BenefitsLevel): string[] {
  return t(`plans.benefits.${level.name.toLowerCase()}`, { returnObjects: true }) as string[];
}

/** A credit count with the active language's digit grouping: "1,875" / "1 875". */
export function formatCredits(credits: number): string {
  return credits.toLocaleString(LOCALES[currentLanguage()]);
}
